import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  DocumentTextIcon,
  EyeIcon,
  TrashIcon,
  PlusCircleIcon,
  FunnelIcon,
} from "@heroicons/react/24/outline";
import { Button, CountryFlag } from "@/shared/ui";
import { getCountryName, ROUTES, formatDateShort } from "@/shared/core";
import { useNavigate } from "react-router-dom";
import type { ClientDocumentAssignment } from "../types/client.types";

interface ClientDocumentsSectionProps {
  documents: ClientDocumentAssignment[];
  onAssignDocument: () => void;
  onRemoveDocument: (documentId: string, documentTitle: string) => void;
  loading?: boolean;
}

export const ClientDocumentsSection: React.FC<ClientDocumentsSectionProps> = ({
  documents,
  onAssignDocument,
  onRemoveDocument,
  loading = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [sourceFilter, setSourceFilter] = useState<
    "all" | "direct" | "from_case"
  >("all");

  const getUniqueDocumentTypes = () => {
    const types = new Set(documents.map((doc) => doc.documents.document_type));
    return Array.from(types).sort();
  };

  const filteredDocuments = documents.filter((doc) => {
    if (typeFilter !== "all" && doc.documents.document_type !== typeFilter) {
      return false;
    }
    if (sourceFilter !== "all" && doc.assignment_type !== sourceFilter) {
      return false;
    }
    return true;
  });

  const getStatusBadge = (status: string) => {
    const colors = {
      draft: "bg-amber-100 text-amber-800",
      final: "bg-atlantis-100 text-atlantis-800",
      archived: "bg-tiber-100 text-tiber-800",
    };
    return colors[status as keyof typeof colors] || "bg-tiber-100 text-tiber-800";
  };

  const getAssignmentTypeBadge = (type: "direct" | "from_case") => {
    return type === "direct" ? (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-tiber-100 text-tiber-800">
        {t("lawyer.clientDocuments.direct")}
      </span>
    ) : (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-tiber-100 text-tiber-800">
        {t("lawyer.clientDocuments.fromCase")}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-lg border border-tiber-100 p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-tiber-200 rounded w-3/4"></div>
          <div className="h-4 bg-tiber-200 rounded w-1/2"></div>
          <div className="h-4 bg-tiber-200 rounded w-5/6"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-lg border border-tiber-600">
      {/* Header with Actions */}
      <div className="p-6 border-b border-tiber-200">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-tiber-900">
              {t("lawyer.clientDocuments.title")}
            </h2>
            <p className="text-sm text-tiber-600 mt-1">
              {t("lawyer.clientDocuments.subtitle")}
            </p>
          </div>
          <Button
            onClick={onAssignDocument}
            variant="primary"
            leftIcon={<PlusCircleIcon className="h-5 w-5" />}
          >
            {t("lawyer.clientDocuments.assign")}
          </Button>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-tiber-700 mb-1 flex items-center gap-2">
              <FunnelIcon className="h-4 w-4" />
              {t("lawyer.clientDocuments.filterByType")}
            </label>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="w-full px-4 py-2 border border-tiber-300 rounded-lg focus:ring-2 focus:ring-tiber-500 focus:border-transparent"
            >
              <option value="all">
                {t("lawyer.clientDocuments.allTypes")}
              </option>
              {getUniqueDocumentTypes().map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-sm font-medium text-tiber-700 mb-1 flex items-center gap-2">
              <FunnelIcon className="h-4 w-4" />
              {t("lawyer.clientDocuments.filterBySource")}
            </label>
            <select
              value={sourceFilter}
              onChange={(e) =>
                setSourceFilter(
                  e.target.value as "all" | "direct" | "from_case"
                )
              }
              className="w-full px-4 py-2 border border-tiber-300 rounded-lg focus:ring-2 focus:ring-tiber-500 focus:border-transparent"
            >
              <option value="all">
                {t("lawyer.clientDocuments.allSources")}
              </option>
              <option value="direct">
                {t("lawyer.clientDocuments.directOnly")}
              </option>
              <option value="from_case">
                {t("lawyer.clientDocuments.fromCaseOnly")}
              </option>
            </select>
          </div>
        </div>
      </div>

      {/* Documents List */}
      <div className="p-6">
        {filteredDocuments.length === 0 ? (
          <div className="text-center py-12">
            <DocumentTextIcon className="h-16 w-16 text-titan mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-juridico mb-2">
              {t("lawyer.clientDocuments.empty.title")}
            </h3>
            <p className="text-tiber-700 mb-6">
              {typeFilter !== "all" || sourceFilter !== "all"
                ? t(
                    "lawyer.clientDocuments.empty.filtered",
                    "Try adjusting the filters"
                  )
                : t(
                    "lawyer.clientDocuments.empty.description",
                    "Assign documents to this client to get started"
                  )}
            </p>
            {typeFilter === "all" && sourceFilter === "all" && (
              <Button
                onClick={onAssignDocument}
                variant="primary"
                leftIcon={<PlusCircleIcon className="h-5 w-5" />}
              >
                {t("lawyer.clientDocuments.assignFirst")}
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {filteredDocuments.map((doc, index) => (
              <motion.div
                key={doc.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="border border-tiber-200 rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <DocumentTextIcon className="h-5 w-5 text-tiber-600" />
                      <h3 className="text-lg font-semibold text-juridico">
                        {doc.documents.title}
                      </h3>
                    </div>

                    <div className="flex items-center gap-3 flex-wrap mb-3">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(
                          doc.documents.status
                        )}`}
                      >
                        {t(`lawyer.documents.status.${doc.documents.status}`)}
                      </span>
                      {getAssignmentTypeBadge(doc.assignment_type)}
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-tiber-100 text-tiber-800">
                        {doc.documents.document_type}
                      </span>
                      <div className="flex items-center text-xs text-titan">
                        <CountryFlag
                          countryCode={doc.documents.country}
                          size="1em"
                          className="mr-1"
                        />
                        {getCountryName(doc.documents.country)}
                      </div>
                    </div>

                    {doc.assignment_type === "from_case" && doc.cases && (
                      <div className="text-sm text-tiber-700 mb-2">
                        <span className="font-medium">
                          {t("lawyer.clientDocuments.fromCaseLabel")}
                        </span>{" "}
                        {doc.cases.title} ({doc.cases.case_number})
                      </div>
                    )}

                    {doc.notes && (
                      <div className="text-sm text-tiber-700 mb-2">
                        <span className="font-medium">
                          {t("lawyer.clientDocuments.notes")}
                        </span>{" "}
                        {doc.notes}
                      </div>
                    )}

                    <div className="text-xs text-titan">
                      {t("lawyer.clientDocuments.assignedOn")}{" "}
                      {formatDateShort(doc.assigned_at)}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 ml-4">
                    <button
                      onClick={() =>
                        navigate(`${ROUTES.DOCUMENT}/${doc.document_id}`)
                      }
                      className="p-2 text-tiber-600 cursor-pointer hover:bg-tiber-50 rounded-lg transition-colors"
                      title={t("lawyer.clientDocuments.view")}
                    >
                      <EyeIcon className="h-5 w-5" />
                    </button>
                    {doc.assignment_type === "direct" && (
                      <button
                        onClick={() =>
                          onRemoveDocument(doc.document_id, doc.documents.title)
                        }
                        className="p-2 text-red-600 cursor-pointer hover:bg-red-50 rounded-lg transition-colors"
                        title={t("lawyer.clientDocuments.remove")}
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Summary Footer */}
      {filteredDocuments.length > 0 && (
        <div className="px-6 py-4 bg-romance-50 border-t border-tiber-200">
          <div className="flex items-center justify-between text-sm text-tiber-700">
            <span>
              {t("lawyer.clientDocuments.total")}: {filteredDocuments.length}{" "}
              {filteredDocuments.length === 1
                ? t("lawyer.clientDocuments.document")
                : t("lawyer.clientDocuments.documents")}
            </span>
            <span>
              {t("lawyer.clientDocuments.direct")}:{" "}
              {
                filteredDocuments.filter((d) => d.assignment_type === "direct")
                  .length
              }{" "}
              | {t("lawyer.clientDocuments.fromCase")}:{" "}
              {
                filteredDocuments.filter(
                  (d) => d.assignment_type === "from_case"
                ).length
              }
            </span>
          </div>
        </div>
      )}
    </div>
  );
};
